/**
 * FdaLetterPageViewer
 * =============================================================================
 * Phase 7 — renders PDF-sourced FDA-letter sections as page images.
 *
 * On mount (and whenever ``sectionId`` changes) the component fetches
 * ``GET /api/fda-letters/sections/{section_id}/pages``.  Three outcomes:
 *
 *   1. ``pages: []``      → HTML-sourced section.  The component renders
 *                          the parent-supplied ``htmlFallback`` slot.
 *                          Keeps the parent's existing sanitised-HTML
 *                          path intact for non-PDF letters.
 *   2. ``pages: [...]``   → PDF-sourced section.  Each page is rendered
 *                          as a ``<section data-page="N">`` containing
 *                          (a) the page image fetched as an authed blob
 *                          (the BFF rejects un-tokened ``<img src>``),
 *                          (b) an invisible text layer carrying
 *                          ``extracted_text`` for browser Ctrl-F and
 *                          screen readers, and (c) a chunk-highlight
 *                          overlay div positioned at the bbox-derived
 *                          pixel rect when ``activeChunkId`` is set.
 *   3. fetch error        → small inline error; the parent's page still
 *                          renders the surrounding chrome.
 *
 * Highlight + scroll contract:
 *   The parent passes ``activeChunkId`` (e.g. derived from a citation
 *   click).  The viewer fetches the chunk row via
 *   ``/chunks/batch?ids=...``, finds the matching page by ``page_num``,
 *   computes the highlight rect with ``computeHighlightRect`` (PDF
 *   points → image pixels), scrolls the page section into view, and
 *   renders a translucent yellow rectangle over the chunk's bbox.
 *   On bbox=null or page_num=null (HTML chunk / multi-page chunk) the
 *   highlight is silently skipped — the citation still navigates to the
 *   correct letter, just without the bbox overlay.
 *
 * The image bytes endpoint is auth-gated, so we cannot put the proxy URL
 * directly in an ``<img src>``.  Instead we fetch each page's image via
 * ``authApi.getFDALetterPageImage`` (axios with ``responseType: 'blob'``),
 * wrap with ``URL.createObjectURL``, and revoke on unmount.  Lazy
 * loading is preserved via ``loading="lazy"`` on the ``<img>`` plus
 * IntersectionObserver-gated image fetches — pages off-screen don't
 * download until they scroll into view.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { authApi } from '../../services/authApi';
import type {
  FDALetterPageRow,
  FDALetterPageLineSpan,
  FDALetterPageTextSpan,
  FDALetterSectionPagesResponse,
  FDALetterChunkRow,
  FDALetterChunkBatchResponse,
} from '../../types/fdaLetter';
import { computeHighlightRect } from './bboxToPixels';
import {
  buildColoredMatchers,
  withTermMarks,
  type TermMatcher,
} from '../viewer/termMarks';
import PageZoomLightbox from '../viewer/PageZoomLightbox';

/**
 * FdaLetterScaledSpan — renders one parser-emitted text_span as a
 * wrapped transparent block over the page image, sized so the text
 * flows inside the bbox and ``select-text`` makes it draggable +
 * Cmd-F-discoverable.
 *
 * **Why not the SBA-style single-line scaleX approach?** The SBA
 * parser emits LINE-level spans (one bbox per visual text line) —
 * each span fits on one line at fontSize = lineHeight, and scaleX
 * tightens the width.  The FDA parser is different: its embedded-text
 * path emits one span per PyMuPDF *block* (paragraph), and crucially
 * joins the block's lines with double-spaces, NOT ``\n``
 * (``backend/app/parsers/pdf_block_extractor.py``).  The OCR path
 * emits one span for the *entire page* with raw newline breaks.
 *
 * Forcing block-level text into a single nowrap line at fontSize =
 * bbox-height produces a tall stretched vertical bar — exactly the
 * highlight-misalignment bug observed on FDA letter 604 in staging.
 *
 * **What we do here.** Render the span as a wrapped div at an
 * estimated body-text font size that lets the text fill the bbox
 * area when flowed naturally.  Font size is calculated from the
 * bbox area and char count:
 *
 *   ``charCount × (charWidth × fontSize) × lineHeight ≈ bboxArea``
 *   with ``charWidth ≈ 0.5 fontSize``, ``lineHeight ≈ 1.2 fontSize``
 *   → ``fontSize ≈ sqrt(bboxArea / (0.6 × charCount))``
 *
 * The transparent text flows top-to-bottom inside the bbox, the
 * browser's Find-in-Page indexes it, and matches highlight at
 * approximately the right position over the underlying rasterised
 * text.  Not pixel-perfect — true line-level positioning would
 * require a parser change to emit per-line spans (a separate
 * backend effort).
 */
function FdaLetterScaledSpan({
  span,
  pdfWidthPt,
  pdfHeightPt,
  matchers,
}: {
  span: FDALetterPageTextSpan;
  pdfWidthPt: number;
  pdfHeightPt: number;
  matchers: TermMatcher[];
}) {
  const x0 = span.bbox_x0 / pdfWidthPt;
  const y0 = span.bbox_y0 / pdfHeightPt;
  const x1 = span.bbox_x1 / pdfWidthPt;
  const y1 = span.bbox_y1 / pdfHeightPt;

  // Drop the trailing ``\n`` the parser appends (``chunk = text + "\n"``);
  // collapse any other whitespace runs to single spaces.  The parser's
  // block-level spans join lines with double-spaces — we want the
  // browser to wrap on word boundaries, so trimming runs to single
  // spaces is fine and keeps the text length-estimate honest.
  // Some legacy rows carry text-less placeholder spans (bbox 0,0,0,0, char range only).
  const text = (span.text ?? '').replace(/\s+/g, ' ').trim();
  if (text.length === 0) return null;

  // Estimate fontSize so the text fills the bbox area when wrapped.
  // Math in the function docstring.  Clamped to a reasonable range:
  // - lower bound (0.5cqh) keeps single-word headers from disappearing
  //   into invisibly tiny text
  // - upper bound (3.0cqh) keeps short text in a tall bbox (e.g. a
  //   centered single-word block) from blowing up.
  const bboxWidthPt = span.bbox_x1 - span.bbox_x0;
  const bboxHeightPt = span.bbox_y1 - span.bbox_y0;
  const fontSizePt = Math.sqrt(
    (bboxWidthPt * bboxHeightPt) / (0.6 * text.length),
  );
  const rawCqh = (fontSizePt / pdfHeightPt) * 100;
  const fontSizeCqh = Math.min(3.0, Math.max(0.5, rawCqh));

  return (
    <div
      data-testid="fda-overlay-block"
      className="absolute"
      style={{
        left: `${x0 * 100}%`,
        top: `${y0 * 100}%`,
        width: `${(x1 - x0) * 100}%`,
        height: `${(y1 - y0) * 100}%`,
        color: 'transparent',
        fontSize: `${fontSizeCqh}cqh`,
        lineHeight: '1.2',
        // Allow natural word-wrap inside the bbox; clip if the
        // estimate underflows and text overruns.
        whiteSpace: 'normal',
        wordSpacing: '0',
        overflow: 'hidden',
        userSelect: 'text',
        cursor: 'text',
      }}
    >
      {withTermMarks(text, matchers)}
    </div>
  );
}

/**
 * FdaLetterScaledLine — pixel-perfect transparent overlay for a single
 * visual text line, sized to the line's PDF-point bbox.
 *
 * Lifted from the SBA viewer's ``ScaledSpan``
 * (``frontend/src/components/viewer/PageRenderer.tsx``): position the
 * span at ``left/top/width/height`` as percentages of the page
 * container, set ``fontSize`` in ``cqh`` (container-query height) so it
 * scales with the rasterised image, render with ``whiteSpace: nowrap``,
 * then measure the natural text width via ``scrollWidth`` and apply
 * ``transform: scaleX(target/natural)`` so the transparent text
 * stretches/compresses to fit the bbox exactly.
 *
 * Reached only via ``FdaLetterTextOverlay`` when ``span.lines`` is
 * non-empty (Phase-A-backfilled pymupdf block spans).  Legacy block
 * spans and OCR spans go through ``FdaLetterScaledSpan`` (wrapped div).
 */
function FdaLetterScaledLine({
  line,
  pdfWidthPt,
  pdfHeightPt,
  matchers,
}: {
  line: FDALetterPageLineSpan;
  pdfWidthPt: number;
  pdfHeightPt: number;
  matchers: TermMatcher[];
}) {
  const ref = useRef<HTMLSpanElement>(null);

  const x0 = line.bbox_x0 / pdfWidthPt;
  const y0 = line.bbox_y0 / pdfHeightPt;
  const w = (line.bbox_x1 - line.bbox_x0) / pdfWidthPt;
  const h = (line.bbox_y1 - line.bbox_y0) / pdfHeightPt;
  // Font size = line bbox height expressed as a percentage of the
  // container (rasterised image) height, in ``cqh`` units.  This matches
  // the SBA ScaledSpan approach.
  const fontSizeCqh = h * 100;

  // After mount (and on every line change), measure the natural text
  // width and apply scaleX so the transparent line fills its bbox
  // horizontally.  Exactly mirrors SBA ScaledSpan
  // (frontend/src/components/viewer/PageRenderer.tsx) — measures via
  // ``getBoundingClientRect()`` on an unsized ``<span>``, then computes
  // target width as ``w * parentContainerWidth``. Working on a span
  // (not a div) matters: absolutely-positioned ``<span>`` elements are
  // the same pattern PDF.js uses for its text layer, and they let the
  // browser's Find-in-Page treat adjacent lines as part of the same
  // text run — Cmd-F finds phrases that cross a visual line boundary.
  // A ``<div>`` per line would break that because each div becomes a
  // separate block-level text island.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Reset to scaleX(1) so we measure the unscaled natural width.
    el.style.transform = 'scaleX(1)';
    const natural = el.getBoundingClientRect().width;
    const container = el.parentElement?.getBoundingClientRect().width;
    if (!natural || !container) return;
    const target = w * container;
    if (natural > 0 && target > 0) {
      el.style.transform = `scaleX(${target / natural})`;
    }
  }, [line, w]);

  return (
    <span
      ref={ref}
      data-testid="fda-overlay-line"
      className="absolute"
      style={{
        left: `${x0 * 100}%`,
        top: `${y0 * 100}%`,
        height: `${h * 100}%`,
        color: 'transparent',
        fontSize: `${fontSizeCqh}cqh`,
        lineHeight: '1',
        whiteSpace: 'nowrap',
        transformOrigin: 'top left',
        userSelect: 'text',
        cursor: 'text',
      }}
    >
      {withTermMarks(line.text ?? '', matchers)}
    </span>
  );
}

/**
 * FdaLetterTextOverlay — per-span dispatcher that picks the
 * pixel-perfect ``FdaLetterScaledLine`` path when the span carries a
 * non-empty ``lines`` array, and falls back to the wrapped-``<div>``
 * ``FdaLetterScaledSpan`` otherwise.
 *
 * Dispatch is per-span, never per-page (plan C2): a single page can
 * carry a mix of Phase-A-backfilled block+lines spans, legacy block
 * spans (not yet backfilled), and OCR full-page spans — each one routes
 * through the correct renderer independently.  No geometric heuristic
 * (bbox height, line count, etc.) is used; the explicit ``span.lines``
 * marker is the only signal.
 */
function FdaLetterTextOverlay({
  span,
  pdfWidthPt,
  pdfHeightPt,
  matchers,
}: {
  span: FDALetterPageTextSpan;
  pdfWidthPt: number;
  pdfHeightPt: number;
  matchers: TermMatcher[];
}) {
  if (span.lines && span.lines.length > 0) {
    return (
      <>
        {span.lines.map((line, i) => (
          <FdaLetterScaledLine
            key={i}
            line={line}
            pdfWidthPt={pdfWidthPt}
            pdfHeightPt={pdfHeightPt}
            matchers={matchers}
          />
        ))}
      </>
    );
  }
  return (
    <FdaLetterScaledSpan
      span={span}
      pdfWidthPt={pdfWidthPt}
      pdfHeightPt={pdfHeightPt}
      matchers={matchers}
    />
  );
}

export interface FdaLetterPageViewerProps {
  /** FDA letter section id (the M2M between letter and doc_type page set). */
  section_id: number;
  /**
   * Slot rendered when the section is HTML-sourced (``pages: []``).
   * The parent owns the legacy sanitised-HTML render path; the viewer
   * delegates to it so we keep one render contract for HTML-only
   * letters (consent decree text emails, etc.). */
  htmlFallback?: React.ReactNode;
  /**
   * Optional chunk id to highlight + scroll to.  Setting this drives a
   * one-shot ``/chunks/batch`` fetch; clearing it removes the overlay
   * but keeps the page scroll position. */
  activeChunkId?: number | null;
  /**
   * Optional callback when a page section becomes visible — useful for
   * the parent to update a "page N of M" indicator.  Wired via
   * IntersectionObserver. */
  onPageVisible?: (pageNum: number) => void;
  /**
   * Searched keyword(s) to box at word level inside the transparent text
   * layer (keyword Compare). Each entry is matched against the page's OCR
   * span text and wrapped in a colored ``<mark>`` over the page image —
   * the SAME matcher the regulatory viewer (PageRenderer) uses. Additive
   * to the ``activeChunkId`` bbox highlight. */
  highlightTerms?: string[] | null;
  englishExact?: boolean;
  /**
   * Per-term mark colors (parallel to ``highlightTerms`` by index). When
   * present, each query row's match gets its own translucent color;
   * when absent, matches stay amber. */
  highlightColors?: (string | undefined)[] | null;
}

type FetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; pages: FDALetterPageRow[] }
  | { status: 'error'; error: string };

/** Inline spinner matching the rest of the FDA-letter UI chrome. */
const Spinner: React.FC<{ label?: string }> = ({ label }) => (
  <div
    className="flex items-center gap-2 py-4 text-sm text-slate-500"
    role="status"
  >
    <svg
      className="w-4 h-4 animate-spin"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
    {label ?? 'Loading...'}
  </div>
);

/** One page: image (auth-fetched as blob) + hidden text + optional highlight. */
const PageImage: React.FC<{
  page: FDALetterPageRow;
  highlight: { left: number; top: number; width: number; height: number } | null;
  /** Per-term matchers used to box searched keyword(s) in the page text layer. */
  matchers: TermMatcher[];
  onVisible?: (pageNum: number) => void;
  /** When set, the page shows a magnifier affordance and clicking the page
   *  image opens the zoom lightbox for this page. */
  onZoomClick?: (pageNum: number) => void;
}> = ({ page, highlight, matchers, onVisible, onZoomClick }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [imgError, setImgError] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const startedFetchRef = useRef(false);

  // IntersectionObserver: trigger the (one-shot) authed blob fetch only when
  // the page section scrolls into view.  Without this, opening a 30-page
  // letter would download 30 images on mount — defeating the lazy story.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    let cancelled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (onVisible) onVisible(page.page_num);
          if (startedFetchRef.current) continue;
          startedFetchRef.current = true;
          authApi
            .getFDALetterPageImage(page.page_id)
            .then((blob) => {
              if (cancelled) return;
              const url = URL.createObjectURL(blob);
              setBlobUrl(url);
            })
            .catch((e: unknown) => {
              if (cancelled) return;
              const msg =
                e && typeof e === 'object' && 'message' in e
                  ? String((e as { message: unknown }).message)
                  : String(e);
              setImgError(msg);
            });
        }
      },
      { rootMargin: '200px 0px' }, // start fetching ~one page before visible
    );
    observer.observe(node);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [page.page_id, page.page_num, onVisible]);

  // Revoke object URL when the page or component goes away — without
  // this we leak one bitmap per visited page until the tab closes.
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  // Aspect ratio reserves layout space BEFORE the image loads, so
  // lazy-loaded pages don't cause jarring scroll-jumps as they fill in.
  const aspectRatio =
    page.image_width_px && page.image_height_px
      ? page.image_width_px / page.image_height_px
      : 8.5 / 11;

  return (
    <section
      ref={sectionRef}
      data-page={page.page_num}
      data-page-id={page.page_id}
      className="relative mb-4 shadow-sm border border-slate-200 bg-white scroll-mt-20"
      style={{ aspectRatio: String(aspectRatio) }}
    >
      {/* Page image — falls back to a soft placeholder until the
          authed blob fetch resolves.  ``alt`` includes the page number
          so screen readers get a sensible label even before the hidden
          text layer below is rendered. */}
      {blobUrl ? (
        <img
          src={blobUrl}
          alt={`Page ${page.page_num + 1}`}
          className={`block w-full h-full object-contain${onZoomClick ? ' cursor-zoom-in' : ''}`}
          draggable={false}
          onClick={onZoomClick ? () => onZoomClick(page.page_num) : undefined}
        />
      ) : imgError ? (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-red-600">
          Failed to load page {page.page_num + 1}: {imgError}
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400">
          Loading page {page.page_num + 1}…
        </div>
      )}

      {/* Positioned text layer — transparent spans overlaid on the
          page image at their original PDF-point positions.  This makes
          (a) browser Cmd/Ctrl-F find-in-page work AND visually highlight
          the match on top of the image, and (b) mouse drag-to-select
          work just like the page is real text.  Mirrors the SBA
          viewer's <ScaledSpan> pattern from
          ``frontend/src/components/viewer/PageRenderer.tsx``.

          Bboxes come in PDF points (not normalised), so we divide by
          ``pdf_width_pt`` / ``pdf_height_pt`` to get 0-1 coords for
          percentage positioning.  Falls back to the legacy flat-text
          hidden layer if (a) spans aren't loaded yet (older pages
          predate the chunker-side text_spans backfill) or (b) the
          page row has no PDF dimensions to normalise against. */}
      {page.text_spans && page.text_spans.length > 0
                       && page.pdf_width_pt && page.pdf_height_pt ? (
        <div
          className="absolute inset-0 select-text"
          style={{ zIndex: 10, containerType: 'size' as never }}
        >
          {page.text_spans.map((span, idx) => (
            <FdaLetterTextOverlay
              key={idx}
              span={span}
              pdfWidthPt={page.pdf_width_pt!}
              pdfHeightPt={page.pdf_height_pt!}
              matchers={matchers}
            />
          ))}
        </div>
      ) : page.extracted_text ? (
        // Legacy fallback for pages without positioned text_spans.
        // ``height: 0; overflow: hidden`` keeps it out of the visual
        // flow but the browser's Find-in-Page still indexes it (no
        // bbox-correct highlight though).
        <div
          className="absolute left-0 top-0 w-full overflow-hidden"
          style={{
            height: 0,
            pointerEvents: 'none',
            color: 'transparent',
          }}
        >
          {page.extracted_text}
        </div>
      ) : null}

      {/* Bbox highlight overlay — rendered only when the active chunk
          maps onto THIS page.  ``ring-2`` gives the user a clear edge
          even on dark page renders.  ``highlightByPageId`` only inserts
          a rect when ``image_width_px``/``image_height_px`` are non-null
          and positive, so the ``!`` non-null assertions below are sound
          (TS can't narrow through the Map indirection). */}
      {highlight ? (
        <div
          data-testid={`fda-letter-chunk-highlight-${page.page_num}`}
          className="absolute bg-yellow-200/40 ring-2 ring-yellow-300 pointer-events-none"
          style={{
            // Full page-width band so the highlight never cuts off mid-line;
            // vertical extent still tracks the chunk. Percentages keep the
            // overlay aligned as the responsive container scales.
            left: '0%',
            top: `${(highlight.top / page.image_height_px!) * 100}%`,
            width: '100%',
            height: `${(highlight.height / page.image_height_px!) * 100}%`,
          }}
        />
      ) : null}

      {/* Page number badge — tiny corner chip so users always know
          which page they're on while scrolling. */}
      <div className="absolute bottom-1 right-2 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded pointer-events-none">
        {page.page_num + 1}
      </div>

      {/* Zoom affordance — the transparent text layer covers most direct image
          clicks, so this magnifier button gives a reliable click target. */}
      {onZoomClick && (
        <button
          type="button"
          onClick={() => onZoomClick(page.page_num)}
          title="Zoom into this page"
          aria-label={`Zoom into page ${page.page_num + 1}`}
          className="absolute top-1 left-2 z-30 flex items-center justify-center rounded bg-black/45 px-1.5 py-1 text-white hover:bg-black/70"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16zM11 8v6M8 11h6" />
          </svg>
        </button>
      )}
    </section>
  );
};

const FdaLetterPageViewer: React.FC<FdaLetterPageViewerProps> = ({
  section_id,
  htmlFallback,
  activeChunkId,
  onPageVisible,
  highlightTerms,
  highlightColors,
  englishExact = false,
}) => {
  const [state, setState] = useState<FetchState>({ status: 'idle' });
  const [activeChunk, setActiveChunk] = useState<FDALetterChunkRow | null>(null);
  // Zoom lightbox: index into the loaded ``pages`` array (null = closed).
  const [zoomIdx, setZoomIdx] = useState<number | null>(null);

  // Per-term matchers (built once per terms/colors change), shared with the
  // regulatory viewer via ../viewer/termMarks so the searched keyword(s) get
  // boxed in the transparent OCR text layer over the page image.
  const matchers = useMemo(
    () => buildColoredMatchers(highlightTerms, highlightColors, englishExact),
    [highlightTerms, highlightColors, englishExact],
  );

  // Fetch /pages on mount + whenever section_id changes.  Local
  // ``cancelled`` flag swallows the stale write if the user clicks a
  // different section before this fetch resolves.
  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    setActiveChunk(null);
    setZoomIdx(null);
    authApi
      .getFDALetterSectionPages(section_id)
      .then((data: FDALetterSectionPagesResponse) => {
        if (cancelled) return;
        setState({ status: 'loaded', pages: data.pages ?? [] });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg =
          e && typeof e === 'object' && 'message' in e
            ? String((e as { message: unknown }).message)
            : String(e);
        setState({ status: 'error', error: msg });
      });
    return () => {
      cancelled = true;
    };
  }, [section_id]);

  // Resolve activeChunkId → bbox + page_num via /chunks/batch.  We hit
  // the batch endpoint with a single id for now; the contract supports
  // 200 at a time if we ever batch citation pre-fetches.
  useEffect(() => {
    // Use explicit null/undefined check rather than ``!activeChunkId`` —
    // chunk_id 0 is a valid id in principle (today PG autoincrement
    // starts at 1, but don't bake that into UI logic).
    if (activeChunkId === null || activeChunkId === undefined) {
      setActiveChunk(null);
      return;
    }
    let cancelled = false;
    authApi
      .getFDALetterChunksBatch([activeChunkId])
      .then((data: FDALetterChunkBatchResponse) => {
        if (cancelled) return;
        const row = data.chunks.find((c) => c.chunk_id === activeChunkId);
        setActiveChunk(row ?? null);
      })
      .catch(() => {
        // Soft-fail: a chunk lookup miss should not surface as a banner.
        // The citation click already scrolled to the section in the
        // parent's handler; missing the bbox just means no highlight.
        if (!cancelled) setActiveChunk(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activeChunkId]);

  // Per-page highlight rect — keyed by ``page_id`` so the lookup is O(1)
  // inside the page map below.  Returns ``null`` for pages without a
  // chunk overlay; ``computeHighlightRect`` returns ``null`` for any
  // chunk with NULL bbox / NULL page_num / mismatched page.
  const highlightByPageId = useMemo(() => {
    if (state.status !== 'loaded' || !activeChunk) return new Map<number, null>();
    // Ownership gate: the parent passes the SAME activeChunkId prop to
    // every expanded viewer instance.  Without this check, two viewers
    // for different doc-type sections would both match a chunk whose
    // page_num recycles to 0/1/2 within each doc_type and both draw a
    // highlight on their own pages.  Only the viewer whose section_id
    // owns this chunk should render a highlight.
    if (activeChunk.section_id !== section_id) return new Map<number, null>();
    const out = new Map<
      number,
      { left: number; top: number; width: number; height: number } | null
    >();
    if (activeChunk.page_num === null) return out;
    if (
      activeChunk.bbox_x0 === null ||
      activeChunk.bbox_y0 === null ||
      activeChunk.bbox_x1 === null ||
      activeChunk.bbox_y1 === null
    ) {
      return out;
    }
    const matchPage = state.pages.find(
      (p) => p.page_num === activeChunk.page_num,
    );
    if (!matchPage) return out;
    if (
      matchPage.pdf_width_pt === null ||
      matchPage.pdf_height_pt === null ||
      matchPage.image_width_px === null ||
      matchPage.image_height_px === null
    ) {
      return out;
    }
    const rect = computeHighlightRect({
      bbox: [
        activeChunk.bbox_x0,
        activeChunk.bbox_y0,
        activeChunk.bbox_x1,
        activeChunk.bbox_y1,
      ],
      pdfWidthPt: matchPage.pdf_width_pt,
      pdfHeightPt: matchPage.pdf_height_pt,
      imageWidthPx: matchPage.image_width_px,
      imageHeightPx: matchPage.image_height_px,
    });
    if (rect) out.set(matchPage.page_id, rect);
    return out;
  }, [state, activeChunk]);

  // When the active chunk's page resolves, scroll its <section> into
  // view.  Query by ``data-page-id`` (globally unique across viewers
  // and doc-types) rather than ``data-page`` (page_num recycles to
  // 0/1/2 per doc_type, so two expanded viewers would have multiple
  // matching elements and the first in DOM order — possibly the wrong
  // one — would win).
  useEffect(() => {
    if (
      !activeChunk ||
      activeChunk.page_num === null ||
      activeChunk.page_id === null
    )
      return;
    const target = document.querySelector(
      `section[data-page-id="${activeChunk.page_id}"]`,
    );
    if (target instanceof HTMLElement) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeChunk]);

  const handleVisible = useCallback(
    (pageNum: number) => {
      if (onPageVisible) onPageVisible(pageNum);
    },
    [onPageVisible],
  );

  if (state.status === 'loading' || state.status === 'idle') {
    return <Spinner label="Loading pages..." />;
  }

  if (state.status === 'error') {
    return (
      <div className="text-sm text-red-600 px-6 py-2" role="alert">
        Failed to load page images: {state.error}
      </div>
    );
  }

  // HTML-source fallback — backend returned ``pages: []``.  Delegate
  // to the parent's legacy sanitised-HTML path so the section still
  // renders.  We don't fetch /sections/{id} here — that's the parent's
  // existing concern.
  if (state.pages.length === 0) {
    return <>{htmlFallback ?? null}</>;
  }

  const pages = state.pages;
  const openZoom = (pageNum: number) => {
    const idx = pages.findIndex((p) => p.page_num === pageNum);
    if (idx >= 0) setZoomIdx(idx);
  };

  return (
    <div className="space-y-2">
      {pages.map((page) => (
        <PageImage
          key={page.page_id}
          page={page}
          highlight={highlightByPageId.get(page.page_id) ?? null}
          matchers={matchers}
          onVisible={handleVisible}
          onZoomClick={openZoom}
        />
      ))}

      {/* Zoom lightbox — re-renders the SAME PageImage (auth-fetched page image
          + keyword <mark> term overlays + bbox highlight) for the zoomed page,
          scaled by the lightbox's width transform so highlights stay aligned at
          every zoom level. Prev/Next steps through all the letter's pages. */}
      {zoomIdx !== null && pages[zoomIdx] && (
        <PageZoomLightbox
          pageCount={pages.length}
          index={zoomIdx}
          onIndex={setZoomIdx}
          onClose={() => setZoomIdx(null)}
          pageLabel={(i) => `Page ${pages[i]!.page_num + 1}`}
          renderPage={(i) => {
            const zp = pages[i]!;
            return (
              <PageImage
                key={zp.page_id}
                page={zp}
                highlight={highlightByPageId.get(zp.page_id) ?? null}
                matchers={matchers}
              />
            );
          }}
        />
      )}
    </div>
  );
};

export default FdaLetterPageViewer;
